import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { TestimonialsSection } from "./testimonials-section";
import { AIChatWidget } from "./ai-chat-widget";
import { LanguageSelector } from "./language-selector";
import { SEOHead } from "./seo-head";
import { FloatingQuoteButton } from "./floating-quote-button";
import { NavigationHeader } from "./navigation-header";
import { useTranslation } from 'react-i18next';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { CustomerDataModal } from "@/components/customer-data-modal";
import { useState } from "react";
import { Shield, Zap, Target, MessageSquare, BarChart3, Settings, CheckCircle, XCircle, Phone, Calendar, Star, TrendingUp, Users, Clock, Video, Globe, MapPin, Mail, LogOut, User, Calculator } from "lucide-react";
import houseSolarPanels from "@/assets/house-solar-panels.jpg";
import solarRenovationCompanies from "@/assets/solar-renovation-companies.jpg";
export function MKTNLanding() {
  const { t, ready } = useTranslation();
  const { user, userProfile, signOut } = useAuth();
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleCustomerDataSubmit = async (customerData: any) => {
    setIsProcessingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: 13000, // 130€ 
          currency: 'eur',
          customerData 
        }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        // Open Stripe checkout in new tab
        window.open(data.url, '_blank');
        setShowCustomerModal(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      // Fallback to booking page
      window.location.href = '/reservar-cita?offer=130';
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Don't render until translations are loaded
  if (!ready) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>;
  }
  const services = [{
    icon: Target,
    title: t('services.list.campaigns.title'),
    description: t('services.list.campaigns.description')
  }, {
    icon: Zap,
    title: t('services.list.landing.title'),
    description: t('services.list.landing.description')
  }, {
    icon: MessageSquare,
    title: t('services.list.chatbot.title'),
    description: t('services.list.chatbot.description')
  }, {
    icon: Users,
    title: t('services.list.automation.title'),
    description: t('services.list.automation.description')
  }, {
    icon: BarChart3,
    title: t('services.list.crm.title'),
    description: t('services.list.crm.description')
  }, {
    icon: Settings,
    title: t('services.list.reports.title'),
    description: t('services.list.reports.description')
  }];
  const optionalServices = [{
    icon: Video,
    title: t('optionalServices.list.video.title'),
    description: t('optionalServices.list.video.description')
  }, {
    icon: Globe,
    title: t('optionalServices.list.website.title'),
    description: t('optionalServices.list.website.description')
  }, {
    icon: MapPin,
    title: t('optionalServices.list.seo.title'),
    description: t('optionalServices.list.seo.description')
  }, {
    icon: Mail,
    title: t('optionalServices.list.email.title'),
    description: t('optionalServices.list.email.description')
  }];
  const packages = [{
    name: t('pricing.packages.starter.name'),
    price: t('pricing.packages.starter.price'),
    features: Array.isArray(t('pricing.packages.starter.features', {
      returnObjects: true
    })) ? t('pricing.packages.starter.features', {
      returnObjects: true
    }) as string[] : [],
    popular: false
  }, {
    name: t('pricing.packages.growth.name'),
    price: t('pricing.packages.growth.price'),
    features: Array.isArray(t('pricing.packages.growth.features', {
      returnObjects: true
    })) ? t('pricing.packages.growth.features', {
      returnObjects: true
    }) as string[] : [],
    popular: true
  }, {
    name: t('pricing.packages.premium.name'),
    price: t('pricing.packages.premium.price'),
    features: Array.isArray(t('pricing.packages.premium.features', {
      returnObjects: true
    })) ? t('pricing.packages.premium.features', {
      returnObjects: true
    }) as string[] : [],
    popular: false
  }];
  // SEO content based on current language
  const seoTitle = t('seo.title');
  const seoDescription = t('seo.description');
  const seoKeywords = t('seo.keywords');

  return <div className="min-h-screen bg-background">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      
      {/* Top Navigation */}
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="gradient-dark absolute inset-0"></div>
        <div className="relative container mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1 text-center lg:text-left">

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 text-shadow leading-tight">
                <span className="text-foreground">{t('hero.title.part1')}</span>{' '}
                <span className="text-primary">{t('hero.title.part2')}</span>{' '}
                <span className="text-foreground">{t('hero.title.part3')}</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0">
                {t('hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start">
                <Link to="/reservar-cita">
                  <Button size="lg" className="gradient-gold hover-lift glow-effect text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto w-full sm:w-auto">
                    <Calendar className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
                    <span className="hidden sm:inline">{t('hero.cta.main')}</span>
                    <span className="sm:hidden">{t('hero.cta.mobile')}</span>
                  </Button>
                </Link>
                
                <Link to="/presupuesto-personalizado">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto w-full sm:w-auto">
                    <Calculator className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
                    Presupuesto Personalizado
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="flex-1 flex justify-center mt-8 lg:mt-0">
              <div className="relative">
                <div className="w-64 h-64 sm:w-80 sm:h-80 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl absolute -inset-4"></div>
                <Card className="relative bg-card/80 backdrop-blur border-primary/20 hover-lift overflow-hidden">
                  <div className="relative h-48 sm:h-64">
                    <img 
                      src={houseSolarPanels} 
                      alt="Casa con paneles solares - Garantía de resultados"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent"></div>
                  </div>
                  <CardContent className="p-6 sm:p-8 relative -mt-8">
                    <div className="text-center space-y-4">
                      <Shield className="h-12 sm:h-16 w-12 sm:w-16 text-primary mx-auto" />
                      <h2 className="text-xl sm:text-2xl font-bold">{t('hero.guarantee.title')}</h2>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        {t('hero.guarantee.description')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Section */}
      <section className="py-12 sm:py-20 bg-primary/5">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30 hover-lift glow-effect">
              <CardContent className="p-8 sm:p-12 text-center">
                <Badge className="gradient-gold mb-6 text-sm sm:text-base px-4 py-2">
                  {t('offer.highlight')}
                </Badge>
                
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                  <span className="text-foreground">{t('offer.title').split(' ').slice(0, 2).join(' ')}</span>{' '}
                  <span className="text-primary">{t('offer.title').split(' ').slice(2).join(' ')}</span>
                </h2>
                
                <p className="text-base sm:text-lg text-muted-foreground mb-6">
                  {t('offer.subtitle')}
                </p>
                
                <div className="bg-card/50 backdrop-blur rounded-lg p-6 sm:p-8 mb-8 max-w-2xl mx-auto">
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-4">
                    {t('offer.price')}
                  </div>
                  
                  <ul className="space-y-3 text-left max-w-md mx-auto">
                    {(t('offer.features', { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  size="lg" 
                  className="gradient-gold hover-lift glow-effect text-base sm:text-lg px-8 py-6 h-auto mb-4 w-full sm:w-auto"
                  onClick={() => setShowCustomerModal(true)}
                >
                  <Zap className="mr-2 h-5 w-5" />
                  {t('offer.cta')}
                </Button>
                
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t('offer.guarantee')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-12 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-12 max-w-4xl mx-auto">
            <Card className="bg-destructive/10 border-destructive/20 hover-lift">
              <CardContent className="p-6 sm:p-8 text-center">
                <XCircle className="h-12 sm:h-16 w-12 sm:w-16 text-destructive mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-destructive">{t('problem.title')}</h3>
                <p className="text-base sm:text-lg">
                  {t('problem.description')}
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-primary/10 border-primary/20 hover-lift">
              <CardContent className="p-6 sm:p-8 text-center">
                <CheckCircle className="h-12 sm:h-16 w-12 sm:w-16 text-primary mx-auto mb-4" />
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-primary">{t('solution.title')}</h3>
                <p className="text-base sm:text-lg">
                  {t('solution.description')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {t('services.title').split(' ').slice(0, 2).join(' ')} <span className="text-primary">{t('services.title').split(' ').slice(2).join(' ')}</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {services.map((service, index) => <Card key={index} className="bg-card hover-lift border-border/50">
                <CardContent className="p-4 sm:p-6">
                  <service.icon className="h-10 sm:h-12 w-10 sm:w-12 text-primary mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Optional Services Section */}
      <section className="py-12 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {t('optionalServices.title').split(' ')[0]} <span className="text-primary">{t('optionalServices.title').split(' ')[1]}</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('optionalServices.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {optionalServices.map((service, index) => <Card key={index} className="bg-card hover-lift border-border/50 text-center">
                <CardContent className="p-4 sm:p-6">
                  <service.icon className="h-10 sm:h-12 w-10 sm:w-12 text-accent mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {t('pricing.title').split(' ').slice(0, 2).join(' ')} <span className="text-primary">{t('pricing.title').split(' ').slice(2).join(' ')}</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">
              {t('pricing.subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => <Card key={index} className={`relative hover-lift h-full flex flex-col ${pkg.popular ? 'border-primary bg-primary/5 lg:scale-105 z-10' : 'bg-card'}`}>
                {pkg.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gradient-gold">
                    <Star className="h-3 w-3 mr-1" />
                    {t('pricing.popular')}
                  </Badge>}
                
                <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                  <div className="text-center mb-6">
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">{pkg.name}</h3>
                    <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">
                      {pkg.price}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">{t('pricing.setupFee')}</p>
                  </div>
                  
                  <ul className="space-y-3 mb-6 sm:mb-8 flex-grow">
                    {pkg.features.map((feature, idx) => <li key={idx} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm">{feature}</span>
                      </li>)}
                  </ul>
                  
                  <Link to={`/reservar-cita?plan=${encodeURIComponent(pkg.name)}`} className="mt-auto">
                    <Button className={`w-full ${pkg.popular ? 'gradient-gold hover-lift' : 'gradient-gold hover-lift'}`}>
                      {t('pricing.selectPlan')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <Card className="bg-primary/10 border-primary/20 max-w-4xl mx-auto">
            <CardContent className="p-8 sm:p-12 text-center">
              <Shield className="h-12 sm:h-16 w-12 sm:w-16 text-primary mx-auto mb-6" />
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t('exclusivity.title')}</h2>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6">
                {t('exclusivity.description')}  
                
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {t('exclusivity.features.limited')}
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {t('exclusivity.features.guaranteed')}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Final CTA Section */}
      <section className="py-12 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
              {t('finalCta.title').split(' ').slice(0, 2).join(' ')} <span className="text-primary">{t('finalCta.title').split(' ').slice(2).join(' ')}</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8">
              {t('finalCta.subtitle')}
            </p>
            
            <Link to="/reservar-cita">
              <Button size="lg" className="gradient-gold hover-lift glow-effect text-base sm:text-lg lg:text-xl px-8 sm:px-12 py-6 sm:py-8 h-auto mb-6 w-full sm:w-auto">
                <Phone className="mr-2 sm:mr-3 h-5 sm:h-6 w-5 sm:w-6" />
                <span className="hidden sm:inline">{t('finalCta.cta.main')}</span>
                <span className="sm:hidden">{t('finalCta.cta.mobile')}</span>
              </Button>
            </Link>
            
            <p className="text-xs sm:text-sm text-muted-foreground px-4">
              {t('finalCta.guarantee')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 bg-background border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <div className="mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-primary mb-2">{t('footer.title')}</h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t('footer.subtitle')}
            </p>
          </div>
          
          <div className="text-xs sm:text-sm text-muted-foreground">
            <p>{t('footer.copyright')}</p>
            <p className="mt-2">
              {t('footer.links')}
            </p>
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <AIChatWidget />
      
      {/* Floating Quote Button */}
      <FloatingQuoteButton />
      
      {/* Customer Data Modal */}
      <CustomerDataModal 
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSubmit={handleCustomerDataSubmit}
        isLoading={isProcessingPayment}
      />
    </div>;
}